import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LogoutButton from '../components/LogoutButton';
import api from '../api';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }

    const fetchProfile = async () => {
      try {
        const res = await api.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        if (errorMessage.includes('jwt expired') || err.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          navigate('/login_page');
        } else {
          setError('خطا در دریافت اطلاعات کاربر');
          setTimeout(() => setError(''), 3000);
        }
      }
    };

    fetchProfile();
  }, [token, navigate]);

  // --- پیام خطا ---
  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 3000);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-red-300 flex items-center gap-3"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
          </svg>
          <span className="font-medium">{error}</span>
        </motion.div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
          <p className="text-gray-600 font-medium">در حال دریافت اطلاعات...</p>
        </motion.div>
      </div>
    );
  }

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
        <div className="max-w-4xl mx-auto">

          {/* عنوان */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-10"
          >
            پروفایل کاربر
          </motion.h2>

          {/* کارت اطلاعات کاربر */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl shadow-xl border border-gray-200 p-6 mb-8"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-4 mb-6 cursor-pointer"
              onClick={() => handleNavigate('/edit-profile_page')}
            >
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {user.user.first_name?.[0] || 'U'}
                </div>
                <div className="absolute inset-0 rounded-full bg-white/20 blur-xl"></div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  {user.user.first_name} {user.user.last_name}
                </h3>
                <p className="text-sm text-indigo-600 font-medium">{user.user.phone_number}</p>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {user.user.referral_code && (
                <ProfileItem
                  label="کد معرف"
                  value={user.user.referral_code}
                  icon={
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M12.95 10.707l.707-.707L8 4.343 6.586 5.757 10.828 10l-4.242 4.243L8 15.657l4.95-4.95z"/>
                    </svg>
                  }
                />
              )}
              <ProfileItem
                label="موجودی طلا"
                value={`${parseFloat(user.balance.balance_gold).toFixed(3)} گرم`}
                icon={
                  <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2l3.09 6.26L18 9.27l-4.5 4.38 1.06 6.2L10 16.79l-5.56 2.92 1.06-6.2L1 9.27l4.91-1.01L10 2z"/>
                  </svg>
                }
                color="from-amber-50 to-yellow-100"
                textColor="text-amber-800"
              />
              <ProfileItem
                label="موجودی ریال"
                value={`${parseFloat(user.balance.balance_rial).toLocaleString('fa-IR')} ریال`}
                icon={
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                  </svg>
                }
                color="from-green-50 to-emerald-100"
                textColor="text-green-800"
              />
            </div>
          </motion.div>

          {/* منوی تنظیمات */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
          >
            <ul className="divide-y divide-gray-200">
              {[
                { label: 'معرفی دوستان', path: '/referral_page', icon: 'share' },
                { label: 'تنظیمات امنیتی', path: '/security_page', icon: 'shield' },
                { label: 'مدیریت حساب بانکی', path: '/bank-accounts_page', icon: 'bank' },
                { label: 'درباره ما', path: '/about_page', icon: 'info' },
                { label: 'اطلاعات نسخه', path: '/version-info', icon: 'version' },
              ].map((item, i) => (
                <motion.li
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.05 }}
                  whileHover={{ backgroundColor: 'rgba(238, 242, 255, 0.5)' }}
                  className="flex items-center justify-between px-6 py-4 cursor-pointer"
                  onClick={() => handleNavigate(item.path)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                      {item.icon === 'share' && (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 2.684a3 3 0 10-6.064 0m6.064 0a9 9 0 10-18 0 9 9 0 0018 0z" />
                        </svg>
                      )}
                      {item.icon === 'shield' && (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-6.618 3.04c-.857.284-1.382.986-1.382 1.748v5.196c0 7.822 9.523 10.176 10.618 3.04C15.477 12.822 21 10.568 21 5.196V0c0-.762-.525-1.464-1.382-1.748z" />
                        </svg>
                      )}
                      {item.icon === 'bank' && (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-4m-6 0H5a2 2 0 002-2v-2m10 4V7m-6 14V7m-6 0h18" />
                        </svg>
                      )}
                      {item.icon === 'info' && (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      {item.icon === 'version' && (
  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
)}
                    </div>
                    <span className="text-gray-800 font-medium">{item.label}</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>

                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="px-6 py-4"
              >
                <LogoutButton className="w-full text-red-600 font-medium text-right flex items-center justify-end gap-2" />
              </motion.li>
            </ul>
          </motion.div>
        </div>
      </div>
    </>
  );
}

// کامپوننت آیتم پروفایل
function ProfileItem({ label, value, icon, color = "from-gray-50 to-gray-100", textColor = "text-gray-800" }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`bg-gradient-to-br ${color} border border-gray-200 rounded-2xl p-4 shadow-sm`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <p className="text-xs text-gray-600">{label}</p>
      </div>
      <p className={`text-lg font-bold ${textColor}`}>{value || '—'}</p>
    </motion.div>
  );
}